import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";

import { useTimezone } from "@web-app/api";
import { Avatar } from "@web-app/components";

import {
  ChatMessage,
  ChatMessageAction,
  ParticipantWithDetails,
} from "../../api/services/chat";

import CallMessageBubble from "./CallMessageBubble";
import VoiceMessageBubble from "./VoiceMessageBubble";
import { isAudioAttachment } from "./chatUtils";
import AIIcon from "./icons/AIIcon";
import ReadDoubleTickIcon from "./icons/ReadDoubleTickIcon";

interface ChatMessagesProps {
  className?: string;
  currentUserId: string;
  currentUserImage?: string | null;
  currentUserName: string;
  dismissedActionMessageIds?: Set<string>;
  hasMoreMessages?: boolean;
  highlightedMessageId?: string | null;
  isAIThinking?: boolean;
  isEscalated?: boolean;
  isLoadingMore?: boolean;
  isSearchMode?: boolean;
  messages: ChatMessage[];
  onLoadMore?: () => void;
  onMessageAction?: (messageId: string, action: ChatMessageAction) => void;
  onMessageClick?: (messageId: string) => void;
  otherPartyImage?: string;
  otherPartyName?: string;
  participantsWithDetails?: ParticipantWithDetails[];
  searchTerm?: string;
}

// Links in AI replies (e.g. a customer's tracking page) must not navigate the
// app away from the chat.
const aiMarkdownComponents: Components = {
  a: ({ children, href }) => (
    <a href={href} rel="noopener noreferrer" target="_blank">
      {children}
    </a>
  ),
};

function ImageWithLoader({ src, alt }: { alt: string; src: string }) {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="relative">
      {isLoading && (
        <div className="flex items-center justify-center bg-gray-200 rounded h-24 w-36 lg:h-32 lg:w-48">
          <div className="animate-spin rounded-full h-6 w-6 lg:h-8 lg:w-8 border-2 border-gray-400 border-t-transparent"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`max-w-full max-h-48 lg:max-h-64 rounded cursor-pointer hover:opacity-90 transition-opacity ${
          isLoading ? "hidden" : "block"
        }`}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
}

export default function ChatMessages({
  className,
  messages,
  currentUserId,
  currentUserImage,
  currentUserName,
  otherPartyImage,
  otherPartyName = "User",
  participantsWithDetails,
  isSearchMode = false,
  searchTerm = "",
  onMessageClick,
  onMessageAction,
  dismissedActionMessageIds,
  isEscalated = false,
  highlightedMessageId,
  hasMoreMessages = false,
  isAIThinking = false,
  isLoadingMore = false,
  onLoadMore,
}: ChatMessagesProps) {
  const getSenderInfo = (senderId: string) => {
    const participant = participantsWithDetails?.find(
      (p) => p.userId === senderId,
    );
    if (participant) {
      return {
        image: participant.image,
        isAI: participant.role === "AI_AGENT",
        name: participant.name,
      };
    }
    return { image: otherPartyImage || "", isAI: false, name: otherPartyName };
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const isFirstRender = useRef(true);
  const prevScrollHeightRef = useRef<number>(0);
  const prevMessagesLengthRef = useRef<number>(0);
  const { userTimezone, convertToTimezone } = useTimezone();

  useEffect(() => {
    if (isFirstRender.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      isFirstRender.current = false;
    } else if (
      containerRef.current &&
      prevScrollHeightRef.current > 0 &&
      messages.length > prevMessagesLengthRef.current
    ) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
      containerRef.current.scrollTop += scrollDiff;
      prevScrollHeightRef.current = 0;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages]);

  const handleLoadMore = () => {
    if (containerRef.current) {
      prevScrollHeightRef.current = containerRef.current.scrollHeight;
    }
    onLoadMore?.();
  };

  useEffect(() => {
    if (highlightedMessageId) {
      // Use setTimeout to ensure DOM has updated before scrolling
      setTimeout(() => {
        const messageElement = messageRefs.current.get(highlightedMessageId);
        if (messageElement) {
          messageElement.scrollIntoView({
            behavior: "instant",
            block: "center",
          });
        }
      }, 0);
    }
  }, [highlightedMessageId]);

  const filteredMessages =
    isSearchMode && searchTerm
      ? messages.filter((msg) =>
          msg.content.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : messages;

  const highlightText = (text: string, search: string) => {
    if (!search || !isSearchMode) return text;

    const parts = text.split(new RegExp(`(${search})`, "gi"));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === search.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200">
              {part}
            </mark>
          ) : (
            part
          ),
        )}
      </>
    );
  };

  if (isSearchMode && searchTerm && filteredMessages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-4">
        <p className="text-gray-400 text-center text-xs lg:text-sm">
          No messages found for "{searchTerm}"
        </p>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-4">
        <p className="text-gray-400 text-center text-xs lg:text-sm">
          Send your first message
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        "flex-1 overflow-y-auto bg-gray-50 p-2 lg:p-4 flex flex-col",
        className,
      )}
    >
      <div className="flex-grow" />
      <div className="space-y-2">
        {hasMoreMessages && (
          <div className="flex justify-center py-1 lg:py-2">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="text-xs lg:text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 lg:px-4 lg:py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              {isLoadingMore ? "Loading..." : "Load older messages"}
            </button>
          </div>
        )}
        {filteredMessages.map((message) => {
          const isOwnMessage = message.senderId === currentUserId;
          const isHighlighted = highlightedMessageId === message.id;

          if (message.messageType === "SERVICE_ALERT") {
            const senderInfo = getSenderInfo(message.senderId);
            return (
              <div
                key={message.id}
                ref={(el) => {
                  if (el) {
                    messageRefs.current.set(message.id, el);
                  } else {
                    messageRefs.current.delete(message.id);
                  }
                }}
                className="flex justify-center my-2"
              >
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-full">
                  {senderInfo.image && (
                    <div className="-mt-0.5">
                      <Avatar
                        image={senderInfo.image}
                        name={senderInfo.name}
                        size={4}
                      />
                    </div>
                  )}
                  <p className="text-xs leading-none text-gray-600 dark:text-gray-300">
                    {message.content}
                  </p>
                </div>
              </div>
            );
          }

          if (message.messageType === "CALL") {
            const callSenderInfo = getSenderInfo(message.senderId);
            return (
              <div
                key={message.id}
                ref={(el) => {
                  if (el) {
                    messageRefs.current.set(message.id, el);
                  } else {
                    messageRefs.current.delete(message.id);
                  }
                }}
              >
                <CallMessageBubble
                  currentUserId={currentUserId}
                  currentUserImage={currentUserImage}
                  currentUserName={currentUserName}
                  isOwnMessage={isOwnMessage}
                  message={message}
                  otherPartyImage={callSenderInfo.image}
                  otherPartyName={callSenderInfo.name}
                />
              </div>
            );
          }

          const senderInfo = getSenderInfo(message.senderId);

          return (
            <div
              key={message.id}
              ref={(el) => {
                if (el) {
                  messageRefs.current.set(message.id, el);
                } else {
                  messageRefs.current.delete(message.id);
                }
              }}
              onClick={() => isSearchMode && onMessageClick?.(message.id)}
              className={`flex gap-1.5 lg:gap-2 ${isOwnMessage ? "justify-end" : "justify-start"} ${
                isSearchMode ? "cursor-pointer hover:opacity-80" : ""
              } ${isHighlighted ? "animate-pulse" : ""}`}
            >
              {!isOwnMessage &&
                (senderInfo.isAI ? (
                  <AIIcon className="w-6 h-6" />
                ) : (
                  <Avatar
                    image={senderInfo.image}
                    name={senderInfo.name}
                    size={6}
                  />
                ))}
              <div
                className={`max-w-[75%] lg:max-w-[70%] px-3 py-1.5 lg:px-4 lg:py-2 ${
                  isOwnMessage
                    ? "bg-[#1E293B] text-white rounded-tl-xl rounded-tr-xl rounded-bl-xl"
                    : "bg-[#E5E7EB] text-gray-900 rounded-tl rounded-tr-xl rounded-br-xl"
                } ${isHighlighted ? "ring-2 ring-blue-500" : ""}`}
              >
                {message.content &&
                  message.content.trim() &&
                  (senderInfo.isAI ? (
                    <div className="text-xs break-words prose prose-xs max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                      <ReactMarkdown components={aiMarkdownComponents}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-xs break-words">
                      {highlightText(message.content, searchTerm)}
                    </p>
                  ))}
                {message.attachments && message.attachments.length > 0 && (
                  <div
                    className={`${message.content && message.content.trim() ? "mt-2" : ""} space-y-2`}
                  >
                    {message.attachments.map((attachment, index) => {
                      const isImage = attachment.fileType?.startsWith("image/");
                      const isAudio = isAudioAttachment(attachment);

                      if (isAudio) {
                        return (
                          <VoiceMessageBubble
                            key={index}
                            attachment={attachment}
                            isOwnMessage={isOwnMessage}
                          />
                        );
                      }

                      if (isImage) {
                        return (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <ImageWithLoader
                              src={attachment.url}
                              alt={attachment.fileName}
                            />
                          </a>
                        );
                      }

                      return (
                        <a
                          key={index}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs underline block truncate ${
                            isOwnMessage ? "text-blue-100" : "text-blue-600"
                          }`}
                        >
                          {attachment.fileName}
                        </a>
                      );
                    })}
                  </div>
                )}
                {!isEscalated &&
                  !isOwnMessage &&
                  !dismissedActionMessageIds?.has(message.id) &&
                  message.suggestedActions?.length && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {message.suggestedActions.map((action) => (
                        <button
                          key={action.type}
                          onClick={() => onMessageAction?.(message.id, action)}
                          className={
                            action.type === "ESCALATE_TO_SUPPORT"
                              ? "text-xs font-medium px-3 py-1.5 rounded-lg bg-[#1E293B] text-white hover:bg-[#0F172A]"
                              : "text-xs font-medium px-3 py-1.5 rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          }
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                <div className="flex items-center gap-1.5 lg:gap-2 mt-0.5 lg:mt-1">
                  <p
                    className={`text-[10px] lg:text-xs ${
                      isOwnMessage ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    {convertToTimezone(
                      new Date(message.createdAt),
                      userTimezone,
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  {isOwnMessage && message.sendingStatus === "sending" && (
                    <span className="text-[10px] lg:text-xs text-gray-300 italic">
                      Sending...
                    </span>
                  )}
                  {isOwnMessage && message.sendingStatus === "failed" && (
                    <span className="text-[10px] lg:text-xs text-red-400">
                      Failed
                    </span>
                  )}
                  {isOwnMessage &&
                    message.sendingStatus !== "sending" &&
                    message.sendingStatus !== "failed" &&
                    message.isRead && (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] lg:text-xs text-gray-300">
                          Read
                        </span>
                        <ReadDoubleTickIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                      </div>
                    )}
                </div>
              </div>
              {isOwnMessage && (
                <Avatar
                  image={currentUserImage || ""}
                  name={currentUserName}
                  size={6}
                />
              )}
            </div>
          );
        })}
        {isAIThinking && (
          <div className="flex gap-2 justify-start">
            <AIIcon className="w-8 h-8" />
            <div className="px-4 py-2 bg-[#E5E7EB] rounded-tl rounded-tr-xl rounded-br-xl">
              <span className="text-xs text-gray-500 animate-pulse">
                Thinking...
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
